import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sorter } from "src/utils/sorter";
import {
  eowDocumentStatus,
  getCheckEowFinalized,
  postExportReport,
  submitEowFinalize,
  weeklyReceivingReport
} from "src/api/sp";
import { resetStore } from "src/store/actions";
import { API_STATUS } from "src/config/constants/genericConstants";
import {
  filterInnerData,
  isExpandTrue,
  searchInInnerData
} from "./WeeklyReportUtil";
import {
  IEowDocumentListPayload,
  IEowsFinalizedPayload,
  IEowSubmitPayload
} from "./interface";

export const fetchWeeklyReceiveReport = createAsyncThunk(
  "/reports/weeklyReport",
  (props: any) => weeklyReceivingReport(props)
);

export const postingExportWeeklyReportData = createAsyncThunk(
  "report/postWeeklyReportData",
  (payload: any) => postExportReport(payload)
);

export const checkEowFinalized = createAsyncThunk(
  "/reports/weeklyreceiving/eow/isfinalized",
  (props: IEowsFinalizedPayload) => getCheckEowFinalized(props)
);

export const getEowDocumentStatus = createAsyncThunk(
  "/reports/weeklyreceiving/eow/documentStatus",
  (props: IEowDocumentListPayload) => eowDocumentStatus(props)
);

export const postEowFinalize = createAsyncThunk(
  "/reports/weeklyreceiving/finalize",
  (props: IEowSubmitPayload) => submitEowFinalize(props)
);

const initialState: any = {
  data: [],
  searchData: [],
  physicalInventoryDate: {
    status: API_STATUS.PENDING,
    masterData: [],
    effectiveDate: [],
    gmrEffectiveDate: [],
    isStaticGMRDataGenerated: false,
    gmrMaxDate: undefined
  },
  finalizeStatus: false,
  status: "pending",
  searchLoading: false,
  error: null,
  errorCount: 0,
  eowReport: {
    eowButtonStatus: false,
    eowDocumentData: [],
    eowApiStatus: "pending",
    eowDocumentListApiStatus: "pending",
    eowPostApiStatus: "pending",
    eowFinalizeStatus: false,
    recordedForTheWeek: false
  }
};

const weeklyReportSlice = createSlice({
  name: "Weekly Report",
  initialState,
  reducers: {
    setAccordianData(state, action) {
      state.data = action.payload;
    },
    setSearchLoading(state) {
      state.searchLoading = true;
    },
    setFinalizeStatus(state) {
      state.finalizeStatus = true;
    },
    sortData(state, action) {
      const { sortParam, sortOrder } = action.payload;
      const sortedData = state.data.map((item: any) => {
        if (!Array.isArray(item.innerData)) return item;
        sorter(item.innerData, sortParam, sortOrder);
        return item;
      });
      state.data = sortedData;
    },
    // Search the data based on doc ID or invoice number (notes column)
    searchRecords(state, action) {
      const { searchTerm } = action.payload;
      if (!searchTerm) {
        state.data = isExpandTrue(state.searchData);
      } else {
        const filteredData = searchInInnerData(state.searchData, searchTerm);
        state.data = filterInnerData(filteredData);
      }
      state.searchLoading = false;
    },
    reset() {
      return initialState;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchWeeklyReceiveReport.pending, (state) => {
        state.status = "pending";
        state.errorCount += 1;
      })
      .addCase(fetchWeeklyReceiveReport.fulfilled, (state, { payload }) => {
        state.status = "idle";
        const list = payload?.data?.list;
        // Apply default sort by received date (ASC) on innerData
        state.data = list?.map((item: any) => {
          if (Array.isArray(item.innerData)) {
            sorter(item.innerData, "rcvdDate", "ASC");
          }
          return item;
        });
        state.searchData = payload?.data?.list;
        state.finalizeStatus = payload.data.isFinalized;
        state.errorCount -= 1;
      })
      .addCase(fetchWeeklyReceiveReport.rejected, (state, _action) => {
        state.status = state.errorCount === 1 ? "error" : "pending";
        state.errorCount -= 1;
      })
      .addCase(checkEowFinalized.fulfilled, (state, { payload }) => {
        state.eowReport.eowButtonStatus = payload.data.isFinalized;
        state.eowReport.recordedForTheWeek = payload.data.recordedForTheWeek;
        state.eowReport.eowApiStatus = "idle";
      })
      .addCase(checkEowFinalized.pending, (state) => {
        state.eowReport.eowButtonStatus = true;
        state.eowReport.eowApiStatus = "pending";
      })
      .addCase(checkEowFinalized.rejected, (state) => {
        state.eowReport.eowButtonStatus = true;
        state.eowReport.eowApiStatus = "error";
      })
      .addCase(getEowDocumentStatus.fulfilled, (state, { payload }) => {
        state.eowReport.eowDocumentListApiStatus = "idle";
        state.eowReport.eowDocumentData = payload.data?.data;
      })
      .addCase(getEowDocumentStatus.pending, (state) => {
        state.eowReport.eowDocumentListApiStatus = "pending";
      })
      .addCase(getEowDocumentStatus.rejected, (state) => {
        state.eowReport.eowDocumentListApiStatus = "error";
      })
      .addCase(postEowFinalize.fulfilled, (state, { payload }) => {
        state.eowReport.eowPostApiStatus = "idle";
        state.eowReport.eowFinalizeStatus = payload.success;
      })
      .addCase(postEowFinalize.pending, (state) => {
        state.eowReport.eowPostApiStatus = "pending";
      })
      .addCase(postEowFinalize.rejected, (state, _action) => {
        state.eowReport.eowPostApiStatus = "error";
      });
  }
});

export const {
  setAccordianData,
  sortData,
  searchRecords,
  reset,
  setSearchLoading
} = weeklyReportSlice.actions;

export const selectWeeklyReportData = (state: any) => {
  return state.weeklyReport.data;
};

export const selectWeeklyReportApiStatus = (state: any) => {
  return state.weeklyReport.status;
};

export const selectSearchLoadingStatus = (state: any) => {
  return state.weeklyReport.searchLoading;
};

export default weeklyReportSlice.reducer;
